import { useState } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Alert,
  Image,
  ColorInput,
  FileButton,
  Anchor,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPalette, IconUpload } from '@tabler/icons-react';
import {
  ASSET_SET_STATE_LABELS,
  ASSET_SET_STATE_COLORS,
  FACTORY_ASSET_PLATFORM_COPY,
  type FactoryAssetPlatform,
} from '@/lib/constants/appFactory';
import type { FactoryAssetQuality, FactoryAssetReport, FactoryAssetSetCreateReq } from '@/lib/types/AppFactory';
import { BrandKitUtils } from '../BrandKitUtils';
import { TvBrandKitForm } from './TvBrandKitForm';
import { TvDevicePreviews } from './TvDevicePreviews';
import { AppFactoryUtils } from '../AppFactoryUtils';
import {
  useFactoryAssetSets,
  useCreateAssetSet,
  useDecideAssetSet,
} from '../useAppFactory';

interface AppBrandKitProps {
  appCfgId: number;
  platform: FactoryAssetPlatform;
  disabled?: boolean;
  /** The platform row's title: the TV form's default app name on the tiles. */
  appTitle?: string;
}

/**
 * Brand kit generate-or-upload, generation arm (WS-2.5): logo + palette →
 * the factory renders the platform's full set, validates it mechanically, and
 * returns a proof sheet. Platform-generic since 3.34.0 (the same flow renders
 * Fire TV's brand files and listing art). Approving publishes the set to the
 * app; per-slot manual uploads below remain the upload arm. The Android-family
 * set (firetv) takes the TV graphics v2 inputs (TvBrandKitForm: symbol,
 * wordmark, app name, ground, tile art) and shows the draft on the TV screens
 * with the factory's quality warnings; Roku keeps its single-logo form.
 */
export function AppBrandKit({ appCfgId, platform, disabled, appTitle }: AppBrandKitProps) {
  const copy = FACTORY_ASSET_PLATFORM_COPY[platform];
  const assetSetsQuery = useFactoryAssetSets(appCfgId, platform);
  const createMutation = useCreateAssetSet(appCfgId, platform);
  const decideMutation = useDecideAssetSet(appCfgId, platform);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bgColorHex, setBgColorHex] = useState(BrandKitUtils.PRESETS[0].bgColorHex);
  const [textColorHex, setTextColorHex] = useState(BrandKitUtils.PRESETS[0].textColorHex);
  const [accentColorHex, setAccentColorHex] = useState('');
  const [lastReport, setLastReport] = useState<FactoryAssetReport | null>(null);
  const [lastQuality, setLastQuality] = useState<FactoryAssetQuality[] | null>(null);
  const isTv = platform === 'firetv';

  // Factory unavailable (gateway off / daemon down) — hide the whole card
  // quietly; the manual per-slot uploaders below keep working.
  if (assetSetsQuery.isError) return null;

  const assetSets = assetSetsQuery.data?.assetSets ?? [];
  const pending = assetSets.find((s) => s.state === 'generated');

  const handleLogoChange = (file: File | null) => {
    if (file && file.size > BrandKitUtils.MAX_LOGO_BYTES) {
      notifications.show({
        title: 'File too large',
        message: 'Logo must be under 8MB.',
        color: 'red',
      });
      return;
    }
    setLogoFile(file);
    setLastReport(null);
  };

  const generate = (req: FactoryAssetSetCreateReq) => {
    createMutation.mutate(req, {
      onSuccess: (res) => {
        setLastReport(res.report);
        setLastQuality(res.quality ?? res.assetSet.quality ?? null);
        notifications.show({
          title: 'Brand Kit Generated',
          message: 'Review the preview and approve it to publish.',
          color: 'green',
        });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  };

  const handleGenerate = async () => {
    if (!logoFile) return;
    let logoBase64: string;
    try {
      logoBase64 = await BrandKitUtils.fileToBase64(logoFile);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Could not read the logo file.',
        color: 'red',
      });
      return;
    }
    generate({
      logoBase64,
      bgColorHex,
      textColorHex: textColorHex || undefined,
      accentColorHex: accentColorHex || undefined,
    });
  };

  const confirmApprove = (assetSetId: string) => {
    modals.openConfirmModal({
      title: 'Publish this brand kit?',
      children: <Text size="sm">{copy.approveBody}</Text>,
      labels: { confirm: 'Approve & Publish', cancel: 'Cancel' },
      onConfirm: () =>
        decideMutation.mutate(
          { assetSetId, state: 'approved' },
          {
            onSuccess: () =>
              notifications.show({
                title: 'Brand Kit Published',
                message: copy.publishedMsg,
                color: 'green',
              }),
            onError: (err) =>
              notifications.show({ title: 'Error', message: err.message, color: 'red' }),
          },
        ),
    });
  };

  const handleReject = (assetSetId: string) => {
    decideMutation.mutate(
      { assetSetId, state: 'rejected' },
      {
        onSuccess: () =>
          notifications.show({
            title: 'Draft Discarded',
            message: 'Generate a new brand kit whenever you are ready.',
            color: 'gray',
          }),
        onError: (err) =>
          notifications.show({ title: 'Error', message: err.message, color: 'red' }),
      },
    );
  };

  const warnings = lastReport?.checks.filter((c) => !c.pass && c.level === 'warn') ?? [];
  const qualityMessages = BrandKitUtils.qualityMessages(pending?.quality ?? lastQuality);
  const draftFiles = isTv && pending ? BrandKitUtils.previewFileMap(pending, []) : null;

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="md">
        <Group gap="xs">
          <IconPalette size={20} />
          <Text fw={600} size="md">
            Brand Kit
          </Text>
        </Group>
        <Text size="sm" c="dimmed">
          {copy.generateIntro}
        </Text>

        {/* Pending set awaits the customer's decision */}
        {pending ? (
          <Stack gap="sm">
            <Group gap="xs">
              <Badge variant="light" color={ASSET_SET_STATE_COLORS.generated} size="sm">
                {ASSET_SET_STATE_LABELS.generated}
              </Badge>
              {pending.source === 'manual' && (
                <Badge variant="outline" color="gray" size="sm">
                  Uploaded Set
                </Badge>
              )}
              <Text size="xs" c="dimmed">
                {pending.source === 'manual' ? 'Uploaded' : 'Generated'}{' '}
                {AppFactoryUtils.formatWhen(pending.created)}
              </Text>
            </Group>
            {pending.proofSheetUrl && (
              <Image
                src={pending.proofSheetUrl}
                alt="Brand kit preview"
                radius="sm"
                mah={360}
                fit="contain"
              />
            )}
            {warnings.length > 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                {warnings.map((c) => (
                  <Text size="sm" key={`${c.deliverable}-${c.name}`}>
                    {c.deliverable}: {c.detail || c.name}
                  </Text>
                ))}
              </Alert>
            )}
            {qualityMessages.length > 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="How it will look on a TV" data-testid="tv-quality">
                {qualityMessages.map((m) => (
                  <Text size="sm" key={m.code} c={m.known ? undefined : 'dimmed'}>
                    {m.text}
                  </Text>
                ))}
              </Alert>
            )}
            {draftFiles && <TvDevicePreviews files={draftFiles} />}
            <Group gap="sm">
              <Button
                onClick={() => confirmApprove(pending.assetSetId)}
                loading={decideMutation.isPending}
                disabled={disabled}
              >
                Approve &amp; Publish
              </Button>
              <Button
                variant="default"
                onClick={() => handleReject(pending.assetSetId)}
                loading={decideMutation.isPending}
                disabled={disabled}
              >
                Discard Draft
              </Button>
            </Group>
          </Stack>
        ) : isTv ? (
          <TvBrandKitForm appTitle={appTitle} disabled={disabled} generating={createMutation.isPending} onGenerate={generate} />
        ) : (
          <Stack gap="sm">
            <Group align="flex-end" gap="sm" wrap="wrap">
              <FileButton onChange={handleLogoChange} accept="image/png,image/jpeg">
                {(props) => (
                  <Button variant="default" leftSection={<IconUpload size={14} />} {...props}>
                    {logoFile ? logoFile.name : 'Choose Logo (PNG or JPG)'}
                  </Button>
                )}
              </FileButton>
              <ColorInput label="Background" value={bgColorHex} onChange={setBgColorHex} w={150} />
              <ColorInput label="Text" value={textColorHex} onChange={setTextColorHex} w={150} />
              <ColorInput
                label="Accent"
                placeholder="Optional"
                value={accentColorHex}
                onChange={setAccentColorHex}
                w={150}
              />
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Presets:
              </Text>
              {BrandKitUtils.PRESETS.map((p) => (
                <Button
                  key={p.key}
                  size="compact-xs"
                  variant="light"
                  onClick={() => {
                    setBgColorHex(p.bgColorHex);
                    setTextColorHex(p.textColorHex);
                  }}
                >
                  {p.label}
                </Button>
              ))}
            </Group>
            <Group>
              <Button
                onClick={handleGenerate}
                loading={createMutation.isPending}
                disabled={disabled || !logoFile}
              >
                Generate Brand Kit
              </Button>
              <Text size="xs" c="dimmed">
                A transparent PNG logo gives the best results.
              </Text>
            </Group>
          </Stack>
        )}

        {/* History */}
        {assetSets.filter((s) => s.state !== 'generated').length > 0 && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Previous Sets
            </Text>
            {assetSets
              .filter((s) => s.state !== 'generated')
              .map((s) => (
                <Group key={s.assetSetId} gap="xs">
                  <Badge
                    variant="light"
                    color={ASSET_SET_STATE_COLORS[s.state] ?? 'gray'}
                    size="sm"
                  >
                    {ASSET_SET_STATE_LABELS[s.state] ?? s.state}
                  </Badge>
                  <Text size="xs" c="dimmed">
                    {s.source === 'manual' ? 'Uploaded' : 'Generated'}{' '}
                    {AppFactoryUtils.formatWhen(s.created)}
                  </Text>
                  {s.proofSheetUrl && (
                    <Anchor href={s.proofSheetUrl} target="_blank" rel="noopener noreferrer" size="xs">
                      preview
                    </Anchor>
                  )}
                </Group>
              ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
